import React, {useState} from "react";
import {useQueryClient} from "react-query";
import {Link, useNavigate} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {tokenSelector} from "../../Recoil/Atom/authState";
import * as boardApi from "../../api/boardApi/boardApi";

import {useGetQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import {ResponseList, ResponseRemove} from "../../models/clientModels/interfaces/Response";
import * as Params from "../../models/clientModels/interfaces/apiParams";

import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from "@mui/material";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import MWSkeleton from "../../components/MWSkeleton";
import SubTitle from "../../components/SubTitle";
import {SubMenuGroup} from "../../styledComponents/StyledListContainer";
import BusinessIcon from "@mui/icons-material/Business";
import {KPBoard} from "../../models/serverModels/board/KPBoard";
import {StyledTableContainer, StyledTableHead} from "../../styledComponents/StyledTable";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1200px",
}));

const BoardList = () => {
  const tokenState = useRecoilValue(tokenSelector);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setAlertState = useSetRecoilState(alertStateAtom);

  const {
    data: boardList,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseList<KPBoard>>({
    key: ["BoardList", {token: tokenState}],
    api: () => boardApi.getList({token: tokenState}),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: boardApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("BoardList");
      },
    },
  });

  return (
    <ListContainer>
      <SubTitle subTitle="게시판 목록" />
      <SubMenuGroup>
        <Box></Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<BusinessIcon />}
          onClick={() => {
            navigate(`/Board/Create`);
          }}
        >
          게시판추가
        </Button>
      </SubMenuGroup>
      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell align="center">게시판 제목</TableCell>
              <TableCell align="center">게시글</TableCell>
              <TableCell align="center" width="25%">
                편집
              </TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={4} />
            ) : (
              boardList?.items.map((item, index) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center">
                    <Link to={`/Post/${item.uid}`}>{item.name}</Link>
                  </TableCell>
                  <TableCell align="center">{item.postCount}</TableCell>

                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      style={{marginRight: "12px"}}
                      onClick={() => {
                        navigate(`/Post/Create/${item.uid}`);
                      }}
                    >
                      글작성
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      style={{marginRight: "12px"}}
                      onClick={() => {
                        navigate(`/Board/Edit/${item.uid}`);
                      }}
                    >
                      편집
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => {
                        setAlertState({
                          toggle: true,
                          message: "등록된 게시판을 삭제하시겠습니까?",
                          execution: () => removeMutate({token: tokenState, id: item.uid!}),
                        });
                      }}
                    >
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </ListContainer>
  );
};

export default BoardList;
